/**
 * Display formatters. Dates are UTC so a value does not depend on the
 * machine timezone. Missing and non-finite numbers render as an em dash.
 */

function timeOf(value) {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (value == null || value === '') return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

function finite(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** @param {unknown} value */
export function formatNumber(value) {
  const n = finite(value);
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** @param {unknown} value UTC calendar date, so the label does not follow the machine timezone. */
export function formatDate(value) {
  const t = timeOf(value);
  if (t == null) return '—';
  const date = new Date(t);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/**
 * Short relative time: now, 5m, 3h, 2d. `now` is injectable for tests.
 * @param {unknown} value
 * @param {number} [now]
 */
export function formatRelative(value, now = Date.now()) {
  const t = timeOf(value);
  if (t == null) return '—';
  const secs = Math.round((now - t) / 1000);
  const ahead = secs < 0;
  const abs = Math.abs(secs);
  if (abs < 60) return 'now';
  const step = abs < 3600 ? [60, 'm'] : abs < 86400 ? [3600, 'h'] : abs < 86400 * 14 ? [86400, 'd'] : null;
  if (!step) return formatDate(t);
  const n = Math.floor(abs / step[0]);
  return ahead ? `in ${n}${step[1]}` : `${n}${step[1]}`;
}

/** @param {unknown} value Signed change. Positive gets an up arrow. */
export function formatDelta(value) {
  const n = finite(value);
  if (n == null) return '—';
  const body = formatNumber(Math.abs(n));
  if (n > 0) return `↑ ${body}`;
  if (n < 0) return `↓ ${body}`;
  return body;
}
